/**
 * Copyright 2021 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useLatestCallback from 'use-latest-callback';
import cx from 'classnames';
import Hidden from '@/components/Hidden';
import { Label } from '@/features/i18n';
import { useLocale, useCountry } from '@/hooks/useLocale';
import { countryCodes } from '@/constants/locale';
import { type ReduxState } from '@/types';
import actionTypes from '@/types/action';
import { addToCart } from '@/actions/shelf/shelf';
import { productInfoConstants } from '@/constants/productInfoConstants';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useRouter } from 'next/navigation';
import { ProductNotes } from '../ProductNotes';
import { ProductInfo } from '../ProductInfo';
import { ProductImage } from '../ProductImage';
import { FULFILLMENT_METHODS } from '@/constants/fulfillmentConstants';
import { cartConstants } from '@/constants/cart';
import { routePaths } from '@/constants/routePaths';
import { checkAvailabilityByGroupTypeId } from '@/features/orders/utils/checkAvailabilityByGroupTypeId';
import { getStaticFormattedPrice } from '@/utils/priceHelpers';
import { fulfillmentGroupTypeIds } from '@/constants/fulfillmentConstants';
import { ProductPricingAndAvailability } from '../ProductPricingAndAvailability';
import type { FulfillmentGroup } from '@/types/reduxStore/skuDetails';
import { useDispatch } from '@/hooks/redux/useDispatch';
import usePrevious from '@/utils/usePrevious';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import azCommonStyles from '@/theme/globals.module.scss';
import styles from './styles.module.scss';
import { getProductStrikethroughAnalyticValue } from '@/components/ProductDetail/analytics/getProductStrikethroughAnalyticValue';
import { trackQuantityFulfillmentChange } from '@/utils/analytics/track/trackQuantityFulfillmentChange';
import { trackQuantityChange } from '@/utils/analytics/track/trackQuantityChange';
import { trackFulfillmentChange } from '@/utils/analytics/track/trackFulfillmentChange';
import { trackNearbyStoreUpdate } from '@/utils/analytics/track/trackNearbyStoreUpdate';
import {
  getFulfillmentId,
  getFulfillmentOption,
  getSelectedFulfilmentID,
} from '@/utils/getFulfillmentOption';
import { useHeaderData } from '@/features/header/api/getHeader';
import { useStoreDetailsData } from '@/features/header/api/getStoreDetails';
import { useIsStoreSelected } from '@/features/header/hooks/useIsStoreSelected';
import { useFeatureFlag } from '@/features/globalConfig';
import { useProductList } from '../../../../../context/useProductList';
import type { ProductPriceInfoAddToCartAnalyticsData } from '../../../../../interface';
import type { DealInfo } from '@/types/availability';
import type { ShelfPart } from '@/types/legacy';
import { useQueryClient } from '@tanstack/react-query';
import { sessionStorage } from '@/utils/sessionStorage';
import { useAddToCart } from '@/features/orders/api/postAddToCart';
import { usePatchOrderItem } from '@/features/orders/api/patchOrderItem';
import { NewProductAvailabilitySkeleton } from '@/components/Skeletons/NewFulfillmentSkeleton/NewProductAvailabilitySkeleton';
import { convertOrderTypeToFulfillmentType } from '@/utils/itemHelpers/lineItemHelpers/convertOrderTypeToFulfillmentType';
import { reportCitrusClick } from '@/features/citrus/utils/reportCitrusClick';
import { AttributeChip } from '@/components/AttributeChip';
import { ConfigurableSkuBadge } from '@/features/configurableSku/components/ConfigurableSkuBadge';
import { type ShippingType } from '@/features/fulfillment';
import { selectPreferredFulfillmentOptionForList } from '@/features/fulfillment/api/optionsToDisplay';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { startAffinityCartPrefetch } from '@/features/pdpAffinity/api/getAffinityRelatedProductsACES';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { getFormattedDisplayNotes } from '@/utils/getFormattedDisplayNotes';
import { useLocalCart } from '@/features/cart/context/CartContext';
import { useSyncLocalCart } from '@/features/cart/hooks/useSyncLocalCart';
import { type ProductSkuDetails } from '@/features/product';
import { useMonetateDecisionFlag } from '@/features/kibo/api/getKiboDecision';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { formatAcesYMME } from '@/utils/analytics/vehicleFormatter';

export type ProductPriceInfoContainerComponentProps = {
  productImageAltName: string;
  part: ShelfPart;
  isList: boolean;
  application: string;
  dealInfo: DealInfo;
  cents: string;
  dollars: string;
  hasVehicle: boolean;
  configurableSku: boolean;
  index: number;
  shelfReadyStatus: string;
  isSearchPageType: boolean;
  storeNumber: string | undefined;
  toggleShowYmme: (partGroupId?: string, partGroupName?: string) => void;
  onDealClick?: (
    part: ShelfPart,
    price: string,
    quantity: number,
    dealRef: React.RefObject<HTMLButtonElement | null>,
    fulfillmentGroupId: number | null,
  ) => void;
  resizeShelfGrids?: () => void;
  setWarrantyModal: ({
    isShown,
    showModalFrom,
    warrantyType,
  }: {
    isShown: boolean;
    showModalFrom?: string;
    warrantyType?: string;
  }) => void;
  xFusionQueryId?: string;
  renderSponsoredChild?: React.ReactNode;
  previouslyBoughtDate?: string;
};

const ProductPriceInfoContainerComponent = ({
  productImageAltName,
  part,
  isList,
  application,
  dealInfo,
  cents,
  dollars,
  hasVehicle,
  configurableSku,
  index,
  shelfReadyStatus,
  storeNumber,
  toggleShowYmme,
  onDealClick,
  setWarrantyModal,
  resizeShelfGrids,
  isSearchPageType,
  xFusionQueryId,
  renderSponsoredChild,
  previouslyBoughtDate,
}: ProductPriceInfoContainerComponentProps) => {
  const locale = useLocale();
  const localCart = useLocalCart();
  const uniqueIdForCart = part.uniqueId ? String(part.uniqueId) : undefined;
  const uniqueId = uniqueIdForCart || part.locationFilter || part.partGroupId || '';
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isBopusNewMsgApplicable = useFeatureFlag('IS_BOPUS_MSG_AVAILABLE_US') === 'true';
  const strikeThroughPricingEnabled = useFeatureFlag('SHOW_STRIKE_THROUGH_PRICING') === 'true';
  const productAttributeChipsEnabled = useFeatureFlag('ENABLE_PRODUCT_ATTRIBUTE_CHIPS') === 'true';
  const useGCPRelatedPartsService = useMonetateDecisionFlag('useGCPRelatedPartsService');
  const legacyRouter = useLegacyRouter();
  const router = useRouter();
  const ref = useRef<string | null>(null);
  const skuPricingAndAvailability = part.skuPricingAndAvailability;
  const corePrice = skuPricingAndAvailability?.corePrice ?? 0;
  const storeRetailPrice = skuPricingAndAvailability?.skuAvailabilityInfo.storeRetailPrice ?? 0;
  const categoryId = skuPricingAndAvailability?.categoryId ?? '';
  const productSpecs = part.productSpecs;
  const isMxWeb = locale === countryCodes.mx;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const fitsVehicle = part.vehicleFit;
  const fulfillmentOptions = useMemo(
    () => part.skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions ?? [],
    [part.skuPricingAndAvailability?.skuAvailabilityInfo.fulfillmentOptions],
  );

  const shipToHomeAvailable = !isMxWeb
    ? checkAvailabilityByGroupTypeId(fulfillmentOptions, fulfillmentGroupTypeIds.shipToHome)
    : part.skuPricingAndAvailability?.shipToHomeAvailable;
  const storePickupAvailable = !isMxWeb
    ? checkAvailabilityByGroupTypeId(fulfillmentOptions, fulfillmentGroupTypeIds.storePickup)
    : part.skuPricingAndAvailability?.storePickupAvailable;
  const {
    data: shelfData,
    isSuccess: shelfQuerySuccess,
    productSkuIsFetching,
    productSkuRawQueryResults,
    productSkuPageInformation,
  } = useProductList();
  const sponsoredPartsAmount =
    shelfData?.shelfParts?.filter((part) => !!part.isSponsoredCard).length ?? 0;
  const shopByUrlYear = shelfData?.shopByUrlYear;
  const shopByUrlMake = shelfData?.shopByUrlMake;
  const shopByUrlModel = shelfData?.shopByUrlModel;
  const productIdFromShelfData = shelfData?.productId;
  const [showDealDrawerButton, setShowBundleDealsButton] = useState(false);
  const isDealBadgeEnabled = useForteDecisionFlagWithTrueDefault('dealBadgeEnabled');
  const isShelfYMM = shopByUrlYear && shopByUrlMake && shopByUrlModel;
  const [backupQuantityInCart, setBackupQuantityInCart] = useState(0);
  const quantityInCart = shelfData?.shelfParts?.[index]?.quantityInCart || backupQuantityInCart;
  const shelfCatLabel = shelfData?.shelfCatLabel;
  const updateCartEnabled = useFeatureFlag('UPDATE_CART_ENABLED') === 'true';
  const isNewDealsApiEnabled = useFeatureFlag('IS_NEW_DEALS_API_ENABLED') === 'true';
  const { data: storeDetailsData } = useStoreDetailsData();
  const storeSelected = useIsStoreSelected();
  const isBopusEnabled = storeDetailsData?.bopusEnabled;
  const isBopusMexicoStoreEnabled = locale === countryCodes.mx && isBopusEnabled;
  const [count, setCount] = useState(
    updateCartEnabled || isBopusMexicoStoreEnabled ? quantityInCart : 0,
  );
  const [groupId, setGroupId] = useState<number | null>(null);
  const country = useCountry();

  const updateSkuDetailsCache = (skuId: string) => {
    const pageNumber = productSkuPageInformation[skuId];

    // pageNumber can be undefined at runtime when query hasn't loaded, despite TypeScript narrowing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (pageNumber == null || !productSkuRawQueryResults[pageNumber]) {
      return;
    }

    const skuIds = productSkuRawQueryResults[pageNumber].skuIds;

    const matchingQueries = queryClient.getQueryCache().findAll({
      predicate: (query) => {
        const isProductSkuDetailsQuery = query.queryKey[0] === 'productSkuDetails';
        if (!isProductSkuDetailsQuery) {
          return false;
        }

        const params = query.queryKey[1];
        if (!params || typeof params !== 'object') {
          return false;
        }

        if (!('skuIds' in params)) {
          return false;
        }

        const querySkuIds = params.skuIds;
        if (!Array.isArray(querySkuIds)) {
          return false;
        }

        // Check if any of the query's skuIds match our skuIds batch
        return querySkuIds.some((id: string) => skuIds.includes(id));
      },
    });

    matchingQueries.forEach((query) => {
      const currentData = queryClient.getQueryData<ProductSkuDetails[]>(query.queryKey);

      if (currentData) {
        const updatedData = currentData.map((skuDetailItem) => {
          if (skuDetailItem.skuPricingAndAvailability.skuId === skuId) {
            const currentGroupId = shippingType
              ? convertOrderTypeToFulfillmentType(shippingType)
              : 0;
            const fulfillmentOptions = skuDetailItem.fulfillmentOptions;
            const updatedFulfillmentOptions = fulfillmentOptions.map((option) => {
              if (option.groupId === currentGroupId) {
                return { ...option, selected: true };
              } else {
                return { ...option, selected: false };
              }
            });

            return {
              ...skuDetailItem,
              quantityInCart: count,
              fulfillmentOptions: updatedFulfillmentOptions,
              itemPresentInCart: true,
            };
          }
          return skuDetailItem;
        });

        queryClient.setQueryData(query.queryKey, updatedData);
      }
    });
  };

  const onAddToCartSuccess = () => {
    const targetSkuId = sessionStorage.getItem('skuIdSetStore') ?? part.skuNumber.toString();

    if (!targetSkuId) {
      return;
    }

    updateSkuDetailsCache(targetSkuId);

    setProductEdited(false);
    setProductUpdatedToCart(true);
  };
  const { mutateAsync: patchOrderItem } = usePatchOrderItem({ onAddToCartSuccess });
  const [prevGroupId, setPrevGroupId] = useState<number | null>(null);
  const [preCount, setPreCount] = useState<number | undefined | void>(0);
  const [prevGroupIdInCart, setPrevGroupIdInCart] = useState<number | null>(null);
  const [trackQOHError, setTrackQOHError] = useState(false);
  const staticPrice = getStaticFormattedPrice(
    skuPricingAndAvailability?.configurableSku,
    skuPricingAndAvailability,
  );
  const price =
    count > 0 ? (Number(staticPrice) * count).toFixed(2).toString() : staticPrice.toString();

  const [productEdited, setProductEdited] = useState(false);
  const defaultFulfillmentOption = useFeatureFlag('DEFAULT_FULFILLMENT_OPTION') || '';
  const isElasticEnabled = useFeatureFlag('IS_AFFINITY_ELASTIC_ENABLED') === 'true';
  const productRepositoryId = shelfData?.shelfParts?.[index]?.productRepositoryId;
  const originalPartTypeId = shelfData?.shelfParts?.[index]?.originalPartTypeId;
  const partGroupId = shelfData?.shelfParts?.[index]?.partGroupId;
  const partTerminologyId = shelfData?.shelfParts?.[index]?.partTerminologyId ?? '';
  const { acesEnabled } = useAcesFlags();
  const {
    readyStatus: orderItemReadyStatus,
    quantity: updatedQuantity,
    skuId: updatedSkuId,
  } = useSelector((state: ReduxState) => state.orders.details);
  // Get sister store availability before calling getFulfillmentOption
  const storeOptionForFulfillment = part.fulfillmentOptions?.find(
    (group) => group.groupId === FULFILLMENT_METHODS.STOREORDER,
  )?.groupFulfillmentOptions?.[0];
  const isSisterStoreAvailableForFulfillment =
    storeOptionForFulfillment?.sisterStoreAvailable ?? false;

  const fulfillmentOption = getFulfillmentOption(
    shipToHomeAvailable,
    storePickupAvailable || isSisterStoreAvailableForFulfillment, // Include sister store as "store pickup available"
    defaultFulfillmentOption,
    locale,
  );

  const defaultShippingId = getFulfillmentId(
    shipToHomeAvailable,
    storePickupAvailable,
    defaultFulfillmentOption,
    !!part.availabilityInfoVO?.nddQuantity && part.availabilityInfoVO.nddQuantity > 0,
    !!part.availabilityInfoVO?.sddQuantity && part.availabilityInfoVO.sddQuantity > 0,
    isSisterStoreAvailableForFulfillment,
  );

  const defaultShippingFulfillmentTypeIdOriginal: number | undefined =
    selectPreferredFulfillmentOptionForList(part.fulfillmentOptions, defaultFulfillmentOption);

  const fulfillmentSelectedInCart = part.fulfillmentOptions?.find((option) => option.selected);
  const shippingOptionInCart =
    fulfillmentSelectedInCart &&
    fulfillmentSelectedInCart.groupId === FULFILLMENT_METHODS.STOREORDER
      ? cartConstants.STOREORDER
      : fulfillmentSelectedInCart?.groupId === FULFILLMENT_METHODS.ONLINEORDER
        ? cartConstants.ONLINEORDER
        : undefined;
  // Use the same sister store calculation from above
  const isSisterStoreAvailable = isSisterStoreAvailableForFulfillment;

  // Calculate initial shipping type with proper fallback logic
  const getInitialShippingType = (): ShippingType | undefined => {
    // First priority: shipping option already selected in cart
    if (shippingOptionInCart) {
      return shippingOptionInCart;
    }

    // Second priority: fulfillment option from legacy logic (if it's a valid shipping type)
    if (
      fulfillmentOption === cartConstants.STOREORDER ||
      fulfillmentOption === cartConstants.ONLINEORDER
    ) {
      return fulfillmentOption;
    }

    // Third priority: if sister store is available, default to STOREORDER
    if (isSisterStoreAvailable) {
      return cartConstants.STOREORDER;
    }

    // Fallback: return undefined (no valid shipping type available)
    return undefined;
  };

  const [shippingType, setShippingType] = useState<ShippingType | undefined>(
    getInitialShippingType(),
  );
  const [fulfillmentId, setFulfillmentId] = useState<number | undefined>(defaultShippingId);
  const addToCartMutation = useAddToCart();
  const { data: headerData } = useHeaderData();
  const catalogVehicleId = headerData?.vehicleMap?.catalogVehicleId ?? '';
  const [productUpdatedToCart, setProductUpdatedToCart] = useState(false);
  const [dealClicked, setDealClicked] = useState(false);
  const handleChangeShippingType = useLatestCallback((shippingType: ShippingType) => {
    setShippingType(shippingType);
    handleProductEdited();
  });
  const handleChangeShippingTypeV2 = useLatestCallback(
    (
      shippingType: ShippingType,
      nweGroupId: typeof FULFILLMENT_METHODS.STOREORDER,
      maxAvailableQty: number,
      isSisterStoreAvailable?: boolean,
      fulfillmentId?: number,
    ) => {
      setFulfillmentId(fulfillmentId);
      setShippingType(shippingType);
      setPrevGroupId(groupId);
      setPreCount(prevCount ?? undefined);
      setGroupId(nweGroupId);
      handleProductEdited();
      if (count > maxAvailableQty) {
        if (isBopusNewMsgApplicable) {
          if (isSisterStoreAvailable) {
            setCount(1);
          } else {
            setCount(quantityInCart);
          }
        } else {
          handleQuantityChange(maxAvailableQty);
        }
      }
    },
  );

  const handleProductEdited = useLatestCallback(() => {
    setProductEdited(quantityInCart > 0);
  });

  const isItemPresentInCart = acesEnabled
    ? part.itemPresentInCart && localCart.isLocallyAdded(part.skuNumber.toString(), uniqueId)
    : part.itemPresentInCart;

  useEffect(() => {
    if (fulfillmentSelectedInCart && groupId != null) {
      if (
        Number(count) === quantityInCart &&
        fulfillmentSelectedInCart.groupId !== groupId &&
        !dealClicked
      ) {
        setProductEdited(true);
      } else if (
        Number(count) === quantityInCart &&
        fulfillmentSelectedInCart.groupId === groupId
      ) {
        setProductEdited(false);
      }
    }
  }, [quantityInCart, count, fulfillmentSelectedInCart, groupId, dealClicked]);

  const handleQuantityChange = useLatestCallback((quantity: number) => {
    const newQuantity = quantity ? Math.abs(quantity) : 1;
    setCount(newQuantity);
    if (count !== quantity) {
      handleProductEdited();
    }
    resizeShelfGrids?.();
  });

  const handleAddToCart = async (quantity = 1): Promise<void> => {
    localCart.markItemBeingAdded(part.skuNumber.toString(), uniqueId);
    const fulfillmentChange =
      groupId === FULFILLMENT_METHODS.STOREORDER ? cartConstants.BOPUS : cartConstants.STH;

    const addToCartRequest = {
      sku_id: (!!sessionStorage.getItem('skuIdSetStore') &&
      sessionStorage.getItem('skuIdSetStore') !== undefined &&
      sessionStorage.getItem('skuIdSetStore') !== 'undefined'
        ? sessionStorage.getItem('skuIdSetStore')
        : String(part.skuNumber))!,
      product_id:
        (!!sessionStorage.getItem('productId') &&
        sessionStorage.getItem('productId') !== undefined &&
        sessionStorage.getItem('productId') !== 'undefined'
          ? sessionStorage.getItem('productId')
          : acesEnabled
            ? (part.partGroupId ?? skuPricingAndAvailability?.productId)
            : skuPricingAndAvailability?.productId) ?? '',
      quantity: String(quantity),
      category_id: categoryId,
      order_type: shippingType ?? cartConstants.STOREORDER,
      fulfillmentId: fulfillmentId,
      catalog_vehicle_id: part.catalogVehicleId !== null ? part.catalogVehicleId : '',
      store_number: String(sessionStorage.getItem('storeNumber') ?? storeNumber ?? ''),
      storePickupAvailable,
      xFusionQueryId,
      positionId: part.positionId,
      brandId: part.brandId,
      uniqueId: uniqueIdForCart,
    };

    let analyticsData: ProductPriceInfoAddToCartAnalyticsData | null = null;

    if (!(sessionStorage.getItem('addOrUpdateCart') === 'true')) {
      analyticsData = {
        fulfillmentChange: prevGroupId !== 0 ? fulfillmentChange : '',
        customLinkName: legacyRouter.asPath.includes(routePaths.searchResult)
          ? prevGroupId !== null && prevGroupId !== 0
            ? 'newSearchResultsPageFulfillmentChange'
            : part.isSponsoredCard
              ? 'New Search Results:Sponsored Product:Cart Addition'
              : 'New Search Results:AZ Product:Cart Addition'
          : prevGroupId !== null && prevGroupId !== 0
            ? 'Shelf:Sponsored Product:Cart Addition'
            : 'Shelf:AZ Product:Cart Addition',
        originalPartTypeId,
        productRepositoryId,
        price,
        corePrice,
        brandName: acesEnabled
          ? ((part.brandName || part.oemBrandName) ?? '')
          : (skuPricingAndAvailability?.brandName ?? ''),
        repositoryId: part.repositoryId,
        partNumber: part.partNumber,
        productRecommendationTypeList: part.isSponsoredCard ? 'Sponsored Product' : 'AZ Product',
        shelfCatLabel,
        prodIndex: part.isSponsoredCard ? 'Recommended' : index - sponsoredPartsAmount,
        productStrikeThroughPricing: getProductStrikethroughAnalyticValue(
          part.skuPricingAndAvailability,
          part.skuPricingAndAvailability?.discountedPrice,
          strikeThroughPricingEnabled,
        ),
        name: part.name,
        productRepositoryIdList: [part.skuPricingAndAvailability?.productId],
        productFulfillmentOptionSeenByCustomer: [String(defaultShippingFulfillmentTypeIdOriginal)],
        partGroupId,
        partTerminologyId,
      };
    }

    const presentInCart = acesEnabled
      ? part.itemPresentInCart && localCart.isLocallyAdded(part.skuNumber.toString(), uniqueId)
      : part.itemPresentInCart;

    if (addToCartRequest.product_id && !presentInCart) {
      //TODO: once add to cart is removed from redux add this side effect to the add to cart mutation
      const onAddToCartSuccess = () => {
        const targetSkuId =
          sessionStorage.getItem('skuIdSetStore') ?? skuPricingAndAvailability?.skuId;

        if (!targetSkuId) {
          return;
        }

        updateSkuDetailsCache(targetSkuId);
      };
      const acesYmme = acesEnabled ? formatAcesYMME(headerData?.vehicleMap) : undefined;

      const vehicleSet = !!headerData?.vehicleMap?.vehicleId;
      if (
        acesEnabled &&
        part.partGroupId &&
        (vehicleSet
          ? part.vehicleFitment !== 'DOES_NOT_FIT'
          : part.vehicleFitment !== 'MAY_NOT_FIT' &&
            part.vehicleFitmentLabel !== 'VEHICLE_SPECIFIC')
      ) {
        const vehicleMap = headerData?.vehicleMap;
        const vq = extractVehicleQuestions(vehicleMap?.vehicleQuestionsIds, true);
        const affinityOpts = {
          partGroupId: part.partGroupId,
          country,
          year: vehicleMap?.year ? String(vehicleMap.year) : undefined,
          makeId: vehicleMap?.makeId ? String(vehicleMap.makeId) : undefined,
          modelId: vehicleMap?.modelId ? String(vehicleMap.modelId) : undefined,
          storeId: headerData?.storeNumber,
          vehicleTypeId: vehicleMap?.vehicleTypeId ? String(vehicleMap.vehicleTypeId) : undefined,
          useElasticSearch: isElasticEnabled,
          vehicleQuestions: (vq as string) || undefined,
        };
        startAffinityCartPrefetch(affinityOpts, useGCPRelatedPartsService);
      }

      await dispatch(
        addToCart(
          addToCartMutation,
          addToCartRequest,
          analyticsData,
          onAddToCartSuccess,
          queryClient,
          typeof legacyRouter.query.searchText === 'string'
            ? legacyRouter.query.searchText
            : typeof legacyRouter.query.filterByKeyWord === 'string'
              ? legacyRouter.query.filterByKeyWord
              : undefined,
          xFusionQueryId,
          acesEnabled,
          acesYmme,
        ),
      );
      localCart.addItem(part.skuNumber.toString(), uniqueId);
      localCart.unmarkItemBeingAdded(part.skuNumber.toString(), uniqueId);
      setBackupQuantityInCart(quantity);
      setTrackQOHError(true);
      resizeShelfGrids?.();
    } else {
      void router.push(routePaths.cart);
    }
  };
  const updateCart = useLatestCallback(async (quantity: number = count): Promise<void> => {
    if (sessionStorage.getItem('sister_store_update_type') === 'select_store_and_update_cart') {
      trackNearbyStoreUpdate(
        'Update_Quantity',
        'In-Stock Near You: Set Store and Add to Cart: Quantity Change',
        {
          quantityInCart,
          brandName: acesEnabled
            ? (part.brandName ?? part.oemBrandName ?? '')
            : (skuPricingAndAvailability?.brandName ?? ''),
          corePrice: corePrice,
          shippingType,
          skuNumber: part.skuNumber.toString(),
          partNumber: part.partNumber ?? '',
          productPrice: price,
          requestQuantity: quantityInCart,
          storeId: storeNumber,
          storeSearchTerm: storeNumber,
          productRank: part.isSponsoredCard ? 'Recommended' : Number(index) + 1,
          productRecommendationTypeList: part.isSponsoredCard ? 'Sponsored Product' : 'AZ Product',
          productQuantityList: [quantity.toString()],
          productRequestedQuantityList: [quantity.toString()],
          ...(acesEnabled
            ? {
                partTerminologyId: [partTerminologyId || ''],
                partGroupId: [partGroupId || ''],
              }
            : {
                originalPartTypeId: originalPartTypeId,
                productRepositoryId: productRepositoryId,
              }),
        },
      );
    }

    await patchOrderItem({
      skuId: sessionStorage.getItem('skuIdSetStore') ?? skuPricingAndAvailability?.skuId ?? '',
      productId:
        !!sessionStorage.getItem('productId') &&
        sessionStorage.getItem('productId') !== undefined &&
        sessionStorage.getItem('productId') !== 'undefined'
          ? sessionStorage.getItem('productId')!
          : acesEnabled
            ? part.partGroupId
            : (productIdFromShelfData ??
              part.productId ??
              skuPricingAndAvailability?.productId ??
              ''),
      quantity: quantity.toString(),
      vehicleId: catalogVehicleId,
      storeNumber: sessionStorage.getItem('storeNumber') ?? storeNumber ?? '',
      fulfillmentTypeId: fulfillmentId,
    });
    setBackupQuantityInCart(quantity);

    const fullFillmaxQuantity =
      part.fulfillmentOptions?.find((option) => option.groupId == groupId)?.maxAvailableQty ?? 0;
    if (
      (!prevGroupIdInCart && groupId === FULFILLMENT_METHODS.STOREORDER) ||
      (!prevGroupIdInCart && groupId === FULFILLMENT_METHODS.ONLINEORDER) ||
      (prevGroupIdInCart === groupId && preCount !== quantity && fullFillmaxQuantity !== 1)
    ) {
      if (!sessionStorage.getItem('sister_store_update_type')) {
        trackQuantityChange(
          legacyRouter.asPath.includes(routePaths.searchResult)
            ? 'newSearchResultsPageQuantityChange'
            : 'shelfPageQuantityChange',
          [String(fullFillmaxQuantity)],
          [String(part.skuNumber.toString())],
          [String(quantity)],
        );
      }
      if (
        sessionStorage.getItem('sister_store_update_type') === 'select_store' &&
        !!sessionStorage.getItem('skuIdSetStore')
      ) {
        sessionStorage.removeItem('skuIdSetStore');
      }

      sessionStorage.removeItem('sister_store_update_type');

      if (prevGroupIdInCart !== groupId) {
        setPrevGroupIdInCart(groupId);
      }
    } else if (
      prevGroupIdInCart !== groupId &&
      preCount !== quantity &&
      fullFillmaxQuantity !== 1
    ) {
      trackQuantityFulfillmentChange(
        groupId === FULFILLMENT_METHODS.STOREORDER ? cartConstants.BOPUS : cartConstants.STH,
        'shelfPageFulfillmentAndQuantityChange',
        [String(fullFillmaxQuantity)],
        [String(part.skuNumber.toString())],
        [String(quantity)],
        'fulfillmentAndQuantityChange',
      );
      setPrevGroupIdInCart(groupId);
    } else if (prevGroupIdInCart !== groupId) {
      trackFulfillmentChange(
        groupId === FULFILLMENT_METHODS.STOREORDER ? cartConstants.BOPUS : cartConstants.STH,
        'shelfPageFulfillmentChange',
      );
      setPrevGroupIdInCart(groupId);
    }
  });

  useEffect(() => {
    if (
      orderItemReadyStatus === actionTypes.UPDATE_ORDERITEM_SUCCESS &&
      updatedSkuId === part.skuNumber.toString()
    ) {
      setCount(Number(updatedQuantity));
      setPreCount(Number(updatedQuantity));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItemReadyStatus]);

  useEffect(() => {
    const countNumber = count ? count : quantityInCart;
    setCount(updateCartEnabled || isBopusMexicoStoreEnabled ? countNumber : 0);
    if (
      !productSkuIsFetching &&
      (!shippingType || !fulfillmentId || (shelfQuerySuccess && ref.current !== shelfReadyStatus))
    ) {
      if (quantityInCart > 0) {
        const selectedFulfillmentId =
          (skuPricingAndAvailability &&
            skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions &&
            getSelectedFulfilmentID(
              skuPricingAndAvailability.skuAvailabilityInfo.fulfillmentOptions,
            )) ||
          getSelectedFulfilmentID(
            part.fulfillmentOptions?.reduce(
              (
                a: Array<{ selected: boolean; fulfillmentTypeId: number; availableQty: number }>,
                b,
              ) => [
                ...a,
                ...(b.groupFulfillmentOptions?.map((opt) => ({ ...opt, selected: b.selected })) ??
                  []),
              ],
              [],
            ) ?? [],
          );
        setFulfillmentId(selectedFulfillmentId);
        if (groupId) {
          setShippingType(
            groupId === FULFILLMENT_METHODS.ONLINEORDER
              ? cartConstants.ONLINEORDER
              : cartConstants.STOREORDER,
          );
        }
      } else {
        const isNotAvailable = fulfillmentOptions.every((item: { available: boolean }) => {
          return !item.available;
        });
        if (isNotAvailable && (part.availabilityInfoVO?.numberOfSisterStoresAvailable ?? 0) > 0) {
          setShippingType(cartConstants.STOREORDER);
        }
      }
    }

    ref.current = shelfReadyStatus;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantityInCart, shelfReadyStatus, updateCartEnabled, groupId, productSkuIsFetching]);

  const showPriceCaptionValue = configurableSku;

  const showWarrantyModalWrapper = () => {
    setWarrantyModal({
      isShown: true,
      showModalFrom: productInfoConstants.ProductSpec,
      warrantyType: part.warrantyType,
    });
  };

  const handleDealClick = useLatestCallback(
    (dealRef: React.RefObject<HTMLButtonElement | null>) => {
      setDealClicked(true);
      onDealClick?.(part, price, count, dealRef, groupId);
    },
  );

  const renderProductAttributeChips = (
    <div
      className={`${cx(styles.attributeChipWrapper, {
        [styles.hasVehicle]: hasVehicle,
        [styles.attributeChipListView]: isList && !isMobile,
        [styles.attributeChipGridAndMobile]: isMobile || !isList,
      })} product-attributes-container`}
    >
      <AttributeChip
        locationAttribute={hasVehicle ? part.locationFilter : undefined}
        topAttributes={part.topAttributes || part.productAttributes}
      />
    </div>
  );

  const finalNotes = getFormattedDisplayNotes(part);

  const renderProductNotesComps = () => {
    if (isShelfYMM || hasVehicle) {
      //hide on mdUp for list view
      return (
        //IMPORTANT: Do not change to implementation css, SEO requested that this be completely hidden
        <Hidden implementation="js" mdUp={!!isList}>
          <div
            className={`${
              finalNotes || application ? styles.gridViewNotes : ''
            } product-notes-grid-view`}
            data-testid="product-notes-grid-view"
          >
            <ProductNotes
              isShelfYMM={!!isShelfYMM}
              application={application}
              notes={finalNotes}
              partNumber={part.partNumber ?? ''}
              productName={part.name}
              skuNumber={part.skuNumber}
              warranty={part.warranty}
              locationFilter={part.locationFilter}
              productSpecs={productSpecs}
              storeRetailPrice={storeRetailPrice}
              onProductClick={onProductClick}
              url={part.seoUrl}
              prodIndex={index - sponsoredPartsAmount}
              partGroupId={part.partGroupId}
              brandId={part.brandId}
              positionId={part.positionId}
              uniqueId={part.uniqueId}
              isSearchPageType={isSearchPageType}
              isSponsoredCard={part.isSponsoredCard ?? false}
            />
          </div>
        </Hidden>
      );
    }
  };

  useEffect(() => {
    if (shippingType && groupId) {
      const selectedGroupFulfillmentHasDealsAvailableQty = fulfillmentOptions.some(
        (option) =>
          option.groupTypeId === groupId && option.available && option.dealsAvailableQty > 0,
      );
      const shouldShowButton =
        isDealBadgeEnabled &&
        storeSelected &&
        !!groupId &&
        selectedGroupFulfillmentHasDealsAvailableQty === true;

      //with new deals api we do not need to check with selected group id for deals available qty
      const shouldShowDealsButton =
        isDealBadgeEnabled &&
        storeSelected &&
        !!groupId &&
        (part.skuPricingAndAvailability?.dealAvailable ?? false) &&
        (locale === countryCodes.us ? true : (part.availabilityInfoVO?.bopusQuantity ?? 0) > 0);

      setShowBundleDealsButton(isNewDealsApiEnabled ? shouldShowDealsButton : shouldShowButton);
    }
  }, [
    isDealBadgeEnabled,
    fulfillmentOptions,
    groupId,
    storeSelected,
    shippingType,
    isNewDealsApiEnabled,
    locale,
    part.availabilityInfoVO?.bopusQuantity,
    part.skuPricingAndAvailability?.dealAvailable,
  ]);
  const prevCount = usePrevious(count);
  const prevQuantityInCart = usePrevious(quantityInCart);

  // Sync count when quantityInCart changes externally (e.g., cart page update)
  useEffect(() => {
    if (prevQuantityInCart != null && prevQuantityInCart !== quantityInCart) {
      setCount(updateCartEnabled || isBopusMexicoStoreEnabled ? quantityInCart : 0);
      setProductEdited(false);
    }
  }, [quantityInCart, prevQuantityInCart, updateCartEnabled, isBopusMexicoStoreEnabled]);

  useEffect(() => {
    if (!productSkuIsFetching && part.fulfillmentOptions && !groupId) {
      const newGroupId =
        part.fulfillmentOptions.find((option) => option.selected)?.groupId ??
        FULFILLMENT_METHODS.ONLINEORDER;
      setGroupId(newGroupId);
    }
  }, [part.fulfillmentOptions, productSkuIsFetching, groupId, fulfillmentOption]);
  useEffect(() => {
    // Prevent updating fulfillment type for items already in cart
    if (!quantityInCart && fulfillmentOption) {
      setShippingType(fulfillmentOption);
      setFulfillmentId(defaultShippingId);
      setGroupId(
        fulfillmentOption === cartConstants.STOREORDER
          ? FULFILLMENT_METHODS.STOREORDER
          : FULFILLMENT_METHODS.ONLINEORDER,
      );
    }

    // We need to update the shipping type if it wasn't set and the fulfillment option changes
    // This could happen if page was first rendered without a store
    // The fulfillment option will change to a valid one from empty string after store is set
  }, [fulfillmentOption, quantityInCart, defaultShippingId]);

  const onProductClick = () => {
    sessionStorage.setItem(
      'productRank',
      part.isSponsoredCard ? 'Recommended' : String(index + 1 - sponsoredPartsAmount),
    );
    if (part.oemBrandName) {
      sessionStorage.setItem('oemBrandName', part.oemBrandName);
    }
    if (part.oemPartNumber) {
      sessionStorage.setItem('oemPartNumber', part.oemPartNumber);
    }
    if (part.citrusAdId) {
      void reportCitrusClick(part.citrusAdId);
    }
    sessionStorage.setItem(
      'pdpAffinityContext',
      JSON.stringify({
        recordType: part.recordType,
        vehicleFitment: part.vehicleFitment,
        partGroupId: part.partGroupId,
      }),
    );
  };

  useSyncLocalCart({ part, acesEnabled, uniqueId });

  const queryPageNumber = productSkuPageInformation[part.skuNumber];

  const isFetchingSkuDetails = productSkuRawQueryResults[queryPageNumber]?.isFetching;

  const priceLoaded =
    shelfQuerySuccess && Boolean(part.skuPricingAndAvailability?.skuId) && !isFetchingSkuDetails;

  const isUS = locale !== countryCodes.mx;

  return (
    <div className={styles.prodSectionWrapper}>
      <div
        className={cx(
          isList ? styles.prodSectionContainer : styles.prodSectionContainerGrid,
          styles.newProdSection,
          {
            [styles.prodSectionGridHeightUS]: locale === 'en-US' && !isList && !isMobile,
          },
        )}
        data-testid="productInfoSection"
      >
        {part.isSponsoredCard ? (
          <Hidden mdDown={!part.isSponsoredCard} implementation="js" mdUp>
            <div
              className={cx(styles.sponsoredTextMob, azCommonStyles['az-caption'])}
              data-testid="sponsored-label"
            >
              <Label label="label_ShelfPage_sponsored" />
            </div>
          </Hidden>
        ) : null}
        <div
          className={cx(styles.prodImageContainer, {
            [styles.prodImageList]: isList,
          })}
        >
          <div className={styles.imageAndBadgeContainer}>
            <Hidden mdUp smDown={!part.configurableSkuFlag}>
              <ConfigurableSkuBadge />
            </Hidden>
            <ProductImage
              alt={productImageAltName}
              src={part.itemImageUrl ?? part.productImageUrl}
              url={part.seoUrl}
              onClick={onProductClick}
              prodIndex={index}
              partGroupId={part.partGroupId ?? ''}
              brandId={part.brandId ?? ''}
              positionId={part.positionId}
              uniqueId={part.uniqueId}
              isSearchPageType={isSearchPageType}
              isSponsoredCard={part.isSponsoredCard ?? false}
            />
            {!isList && renderSponsoredChild}
          </div>
          <Hidden mdUp implementation="js">
            <div data-testid="mobile-productInfo" className={styles.mobileProdInfoContainer}>
              <ProductInfo
                application={application}
                dealInfo={dealInfo}
                cents={cents.padEnd(2, '0')}
                dollars={dollars}
                corePrice={corePrice}
                fitsVehicle={fitsVehicle}
                hasVehicle={hasVehicle}
                itemPresentInCart={isItemPresentInCart}
                notes={finalNotes}
                partNumber={part.partNumber ?? ''}
                partGroupId={part.partGroupId}
                partTerminologyId={part.partTerminologyId}
                brandId={part.brandId}
                positionId={part.positionId}
                uniqueId={part.uniqueId}
                partGroupName={part.partTypeName}
                productId={part.skuPricingAndAvailability?.productId}
                productName={part.name}
                productImage={part.productImageUrl}
                toggleShowYmme={toggleShowYmme}
                showPriceCaption={showPriceCaptionValue}
                skuNumber={part.skuNumber}
                url={part.seoUrl}
                onProductClick={onProductClick}
                partsThatFitUrl={part.productCanonicalUrl || part.productSeoUrl || ''}
                warranty={part.warranty || part.warrantyText || ''}
                showWarrantyModalWrapper={showWarrantyModalWrapper}
                recordType={part.recordType}
                locationFilter={part.locationFilter}
                productSpecs={part.productSpecs}
                storeRetailPrice={storeRetailPrice}
                prodIndex={index}
                count={count}
                price={price}
                showDealDrawerButton={showDealDrawerButton}
                onDealClick={handleDealClick}
                clearance={skuPricingAndAvailability?.skuAvailabilityInfo.clearanceItem}
                isMobile={isMobile}
                oemBrandName={part.oemBrandName}
                oemPartNumber={part.oemPartNumber}
                originalPartTypeId={part.originalPartTypeId}
                productBrandList={
                  acesEnabled
                    ? part.oemBrandName || part.brandName || ''
                    : part.oemBrandName || skuPricingAndAvailability?.brandName || ''
                }
                priceLoaded={priceLoaded}
                dealConfigurableLabel={skuPricingAndAvailability?.dealConfigurableLabel}
                fulfillmentId={fulfillmentId}
                shippingType={shippingType ?? cartConstants.STOREORDER}
                setFulfillmentId={setFulfillmentId}
                isSearchPageType={isSearchPageType}
                handleQuantityChange={handleQuantityChange}
                renderProductAttributeChips={renderProductAttributeChips}
                productAttributeChipsEnabled={productAttributeChipsEnabled}
                isSponsoredCard={part.isSponsoredCard ?? false}
                vehicleFitment={part.vehicleFitment}
              />
            </div>
          </Hidden>
        </div>
        <Hidden smDown implementation="js">
          <div
            data-testid="productInfo"
            className={cx({
              [styles.prodInfoContainer]: isList,
              [styles.productInfoGrid]: !isList,
            })}
          >
            <ProductInfo
              application={application}
              dealInfo={dealInfo}
              cents={cents.padEnd(2, '0')}
              dollars={dollars}
              corePrice={corePrice}
              fitsVehicle={fitsVehicle}
              vehicleFitment={part.vehicleFitment}
              hasVehicle={hasVehicle}
              itemPresentInCart={isItemPresentInCart}
              notes={finalNotes}
              partNumber={part.partNumber ?? ''}
              partGroupId={part.partGroupId}
              partTerminologyId={part.partTerminologyId}
              brandId={part.brandId}
              positionId={part.positionId}
              uniqueId={part.uniqueId}
              partGroupName={part.partTypeName}
              productId={part.skuPricingAndAvailability?.productId}
              productName={part.name}
              productImage={part.productImageUrl}
              toggleShowYmme={toggleShowYmme}
              showPriceCaption={showPriceCaptionValue}
              skuNumber={part.skuNumber}
              url={part.seoUrl}
              onProductClick={onProductClick}
              partsThatFitUrl={part.productCanonicalUrl || part.productSeoUrl || ''}
              warranty={part.warranty || part.warrantyText || ''}
              showWarrantyModalWrapper={showWarrantyModalWrapper}
              recordType={part.recordType}
              locationFilter={part.locationFilter}
              productSpecs={part.productSpecs}
              storeRetailPrice={storeRetailPrice}
              prodIndex={index}
              count={count}
              price={price}
              showDealDrawerButton={showDealDrawerButton}
              onDealClick={handleDealClick}
              clearance={skuPricingAndAvailability?.skuAvailabilityInfo.clearanceItem}
              isMobile={isMobile}
              originalPartTypeId={part.originalPartTypeId}
              productBrandList={
                acesEnabled
                  ? part.oemBrandName || part.brandName || ''
                  : part.oemBrandName || skuPricingAndAvailability?.brandName || ''
              }
              oemBrandName={part.oemBrandName}
              oemPartNumber={part.oemPartNumber}
              priceLoaded={priceLoaded}
              dealConfigurableLabel={skuPricingAndAvailability?.dealConfigurableLabel}
              fulfillmentId={fulfillmentId}
              shippingType={shippingType ?? cartConstants.STOREORDER}
              setFulfillmentId={setFulfillmentId}
              isSearchPageType={isSearchPageType}
              handleQuantityChange={handleQuantityChange}
              renderProductAttributeChips={renderProductAttributeChips}
              productAttributeChipsEnabled={productAttributeChipsEnabled}
              isSponsoredCard={part.isSponsoredCard ?? false}
              previouslyBoughtDate={previouslyBoughtDate}
            />
            {!isList && productAttributeChipsEnabled && renderProductAttributeChips}
          </div>
        </Hidden>
        {/* shelf ff option */}
        <div
          className={cx(
            styles.prodPriceContainer,
            styles.prodPriceContainerforFulfillmentStickers,
            {
              [styles.gridView]: !isList,
              [styles.priceContainerList]: isList,
              [styles.gridPriceFitmentSpacing]: !isList && !hasVehicle,
              [styles.noNotesMargin]:
                isMobile && (!(isShelfYMM || hasVehicle) || !(finalNotes || application)),
              [styles.ProdPriceContainerlist]: isUS || isList,
            },
          )}
        >
          {priceLoaded ? (
            <ProductPricingAndAvailability
              cents={cents.padEnd(2, '0')}
              corePrice={corePrice}
              dealInfo={dealInfo}
              configurableSku={configurableSku}
              dollars={dollars}
              fitsVehicle={fitsVehicle}
              vehicleFitment={part.vehicleFitment}
              hasVehicle={hasVehicle}
              itemPresentInCart={isItemPresentInCart}
              productId={skuPricingAndAvailability?.productId}
              productName={part.name}
              productImage={part.productImageUrl}
              shipToHomeAvailable={shipToHomeAvailable}
              skuNumber={part.skuNumber}
              storePickupAvailable={storePickupAvailable}
              skuPricingAndAvailability={skuPricingAndAvailability}
              toggleShowYmme={toggleShowYmme}
              url={part.seoUrl}
              partsThatFitUrl={part.productCanonicalUrl || part.productSeoUrl || ''}
              vehicleSpecific={part.vehicleSpecific ?? false}
              storeSelected={part.storeSelected}
              prodIndex={index}
              sponsoredPartsAmount={sponsoredPartsAmount}
              fulfillmentOptions={part.fulfillmentOptions as FulfillmentGroup[] | undefined}
              price={price}
              shippingType={shippingType ?? cartConstants.STOREORDER}
              productEdited={
                productEdited &&
                (acesEnabled ? localCart.isLocallyAdded(part.skuNumber.toString(), uniqueId) : true)
              }
              count={count}
              updateCart={updateCart}
              handleChangeShippingType={handleChangeShippingType}
              handleChangeShippingTypeV2={handleChangeShippingTypeV2}
              handleAddToCart={handleAddToCart}
              trackQOHError={trackQOHError}
              setTrackQOHError={setTrackQOHError}
              showDealDrawerButton={showDealDrawerButton}
              onDealClick={handleDealClick}
              clearance={skuPricingAndAvailability?.skuAvailabilityInfo.clearanceItem}
              groupId={groupId}
              part={part}
              productUpdatedToCart={productUpdatedToCart}
              setProductUpdatedToCart={setProductUpdatedToCart}
              setFulfillmentId={setFulfillmentId}
              dealConfigurableLabel={part.skuPricingAndAvailability?.dealConfigurableLabel}
              handleQuantityChange={handleQuantityChange}
              fulfillmentId={fulfillmentId}
              renderProductAttributeChips={renderProductAttributeChips}
              productAttributeChipsEnabled={productAttributeChipsEnabled}
            />
          ) : (
            <NewProductAvailabilitySkeleton isList={isList} finalNotes={finalNotes} />
          )}
        </div>
      </div>
      {renderProductNotesComps()}
    </div>
  );
};

export const ProductPriceInfoContainer = ProductPriceInfoContainerComponent;
